import { useMemo } from "react";

import { ReviewPlanPendingArea } from "../components/ReviewPlanVisitsList";
import { UserGroup } from "./useReviewPlanVisitGrouping";

type AreaGroup = {
    plannerArea: ReviewPlanPendingArea["plannerArea"];
    visits: ReviewPlanPendingArea["plannerVisits"];
};

function matchesArea(plannerArea: AreaGroup["plannerArea"], query: string) {
    return (
        plannerArea.name.toLowerCase().includes(query) ||
        plannerArea.project.name.toLowerCase().includes(query) ||
        plannerArea.project.client.name.toLowerCase().includes(query)
    );
}

function matchesFieldworker(visit: AreaGroup["visits"][number], query: string) {
    return visit.fieldworkersStaged.some(({ user }) =>
        `${user.firstName} ${user.lastName}`
            .normalize("NFD")
            .replace(/\p{Diacritic}/gu, "")
            .toLowerCase()
            .includes(query),
    );
}

function filterAreaGroups(areaGroups: AreaGroup[], query: string): AreaGroup[] {
    return areaGroups
        .map(areaGroup => {
            if (matchesArea(areaGroup.plannerArea, query)) {
                return areaGroup;
            }

            const matchingVisits = areaGroup.visits.filter(visit => matchesFieldworker(visit, query));
            return matchingVisits.length > 0 ? { ...areaGroup, visits: matchingVisits } : null;
        })
        .filter((areaGroup): areaGroup is AreaGroup => areaGroup !== null);
}

export function useReviewPlanUserGroupFiltering(userGroups: UserGroup[], searchTerm: string): UserGroup[] {
    return useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        if (!query) return userGroups;

        return userGroups
            .map(group => ({
                ...group,
                toBeCancelled: filterAreaGroups(group.toBeCancelled, query),
                toBePlanned: filterAreaGroups(group.toBePlanned, query),
            }))
            .filter(group => group.toBeCancelled.length > 0 || group.toBePlanned.length > 0);
    }, [userGroups, searchTerm]);
}
